import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import { InputFromList } from 'components/settings/recordList';
import { orderBy, where } from 'firebase/firestore';
import { useAuthState } from 'firebaseStore';
import {
    Commit,
    CommitAction,
    commitCoverter,
    COMMIT_COLLECTION_NAME,
    ContextProviderProps,
    UserProfile,
    userProfileCoverter,
    USER_PROFILE_COLLECTION_NAME,
} from 'models';
import { useNavigate, useParams } from 'react-router-dom';
import { CommitsPageContextProvider, useCommitsPageContext } from './commitsPageContext';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import './commitsPage.css';
import { usePathUrl } from 'components/usersPage/usersPage';

export const getRecordType = (recordType?: string) => {
    if (recordType === 'product-types') {
        return 'ProductType';
    }
    if (recordType === 'languages') {
        return 'Language';
    }
    if (recordType === 'countries') {
        return 'Country';
    }
    if (recordType === 'sections') {
        return 'Section';
    }
    if (recordType === 'clauses') {
        return 'Clause';
    }
    if (recordType === 'coverage') {
        return 'Coverage';
    }
    if (recordType === 'user-profiles') {
        return 'UserProfile';
    }
    throw new Error(`Urecognised record type ${recordType}`);
};

export const typeToWords = (recordType: string) => {
    if (recordType === 'ProductType') {
        return ['product type', 'product types'];
    }
    if (recordType === 'Language') {
        return ['language', 'languages'];
    }
    if (recordType === 'Country') {
        return ['country', 'countries'];
    }
    if (recordType === 'Section') {
        return ['section', 'sections'];
    }
    if (recordType === 'Subsection') {
        return ['subsection', 'subsections'];
    }
    if (recordType === 'Clause') {
        return ['clause', 'clauses'];
    }
    if (recordType === 'Coverage') {
        return ['coverage', 'coverages'];
    }
    if (recordType === 'UserProfile') {
        return ['user profile', 'user profiles'];
    }
    throw new Error(`Urecognised recordType ${recordType}`);
};

const RecordDropdown = () => {
    const { recordType: _recordType } = useParams();
    const recordType = getRecordType(_recordType);
    const navigate = useNavigate();
    const name = recordType === 'ProductType' ? 'Product Type' : recordType;
    return (
        <InputFromList
            id="recordDropdown"
            value={{ name, id: _recordType as string }}
            values={[
                { name: 'Product Type', id: 'product-types' },
                { name: 'Clause', id: 'clauses' },
                { name: 'Coverage', id: 'coverage' },
                { name: 'Section', id: 'sections' },
                { name: 'Country', id: 'countries' },
                { name: 'Language', id: 'languages' },
            ]}
            onSelect={(inputValue) => inputValue && navigate(`/settings/${inputValue.id}/commits`)}
        />
    );
};

const useCommitCollectionProps = (): ContextProviderProps<Commit> => {
    const { recordType: _recordType } = useParams();
    const [, , tenant] = useAuthState();
    const recordType = getRecordType(_recordType);
    const typeIndex = `${tenant}_${recordType}`;

    const filterConstraint =
        recordType === 'Section'
            ? where('typeIndex', 'in', [typeIndex, `${tenant}_Subsection`])
            : where('typeIndex', '==', typeIndex);
    return {
        collectionName: COMMIT_COLLECTION_NAME,
        converter: commitCoverter,
        constraints: [filterConstraint, orderBy('createdAt', 'desc')],
    };
};

export const CommitsPage = () => {
    const providerProps = useCommitCollectionProps();
    return (
        <CollectionProvider {...providerProps}>
            <CollectionProvider
                collectionName={USER_PROFILE_COLLECTION_NAME}
                converter={userProfileCoverter}
                keyFunction={(r) => r.email}
            >
                <CommitsPageContextProvider>
                    <CommitsPageContent />
                </CommitsPageContextProvider>
            </CollectionProvider>
        </CollectionProvider>
    );
};

interface CommitContributorGroup {
    createdBy: string;
    action: CommitAction;
    commits: Commit[];
    typeIndex: string;
}
const makeContributorGroups = (commits: Commit[]): CommitContributorGroup[] => {
    const contributorGroups: CommitContributorGroup[] = [];
    commits.map((commit: Commit) => {
        if (
            contributorGroups.length === 0 ||
            contributorGroups[0].createdBy !== commit.createdBy ||
            contributorGroups[0].action !== commit.action ||
            contributorGroups[0].typeIndex !== commit.typeIndex
        ) {
            return contributorGroups.unshift({
                createdBy: commit.createdBy,
                commits: [commit],
                action: commit.action,
                typeIndex: commit.typeIndex,
            });
        }
        return contributorGroups[0].commits.unshift(commit);
    });
    return contributorGroups;
};

export interface CommitDateGroup {
    daysAgo: number;
    date: Date;
    commits: Commit[];
}

const getDaysAgo = (d: Date) => {
    const today = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
    const commitDate = new Date(d.setHours(0, 0, 0, 0));
    return (today - commitDate.getTime()) / (1000 * 60 * 60 * 24);
};

export const makeDateGroups = (commits: Commit[]): CommitDateGroup[] => {
    const dateGroups: CommitDateGroup[] = [];
    commits
        .map((commit: Commit) => {
            const commitDate = new Date(
                new Date((commit.createdAt?.seconds as number) * 1000).setHours(0, 0, 0, 0)
            );
            return { commitDate, commit };
        })
        .map(({ commitDate, commit }) => {
            if (dateGroups.length === 0) {
                return dateGroups.push({
                    date: commitDate,
                    commits: [commit],
                    daysAgo: getDaysAgo(commitDate),
                });
            }
            if (dateGroups[dateGroups.length - 1].date.getTime() !== commitDate.getTime()) {
                return dateGroups.push({
                    date: commitDate,
                    commits: [commit],
                    daysAgo: getDaysAgo(commitDate),
                });
            }
            return dateGroups[dateGroups.length - 1].commits.unshift(commit);
        });
    return dateGroups;
};

const CommitsPageContent = () => {
    const { collectionName, constraints } = useCommitCollectionProps();
    const [commits] = useCollectionContext(collectionName, constraints) as CollectionState<Commit>;
    const { setExpCol, expCol } = useCommitsPageContext();
    const filteredCommits = commits.filter(
        (commit) =>
            commit.changes.filter((change) =>
                commit.typeIndex.endsWith('Clause')
                    ? change.field === 'content'
                    : change.field === 'name'
            ).length
    );
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#FCFBFE',
                minHeight: '100vh',
                width: '100vw',
            }}
        >
            <div style={{ fontSize: '34px', margin: '20px 0' }}>Change History</div>
            <div
                style={{
                    width: '100%',
                    height: '70px',
                    borderColor: '#EBEDEE',
                    borderStyle: 'solid',
                    borderWidth: '1px 0 1px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <div style={{ width: '240px', margin: '0 128px' }}>
                    <RecordDropdown />
                </div>
                <div
                    style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 128px',
                    }}
                    onClick={() => {
                        const nextAllExpCol =
                            expCol.getValue('all') !== 'expanded' ? 'expanded' : 'collapsed';
                        setExpCol('all', nextAllExpCol);
                    }}
                >
                    {expCol.getValue('all') !== 'expanded' ? 'Expand all' : 'Collapse All'}
                </div>
            </div>
            <div style={{ width: 'calc(100% - 256px)', margin: '76px 128px 0 128px' }}>
                {makeDateGroups(filteredCommits).map((dateGroup) => (
                    <DateGroup {...dateGroup} key={dateGroup.daysAgo} />
                ))}
            </div>
        </div>
    );
};

const ContributorsListImage = ({ createdBy }: { createdBy: string }) => {
    const [, , , userProfileMap] = useCollectionContext(
        USER_PROFILE_COLLECTION_NAME
    ) as CollectionState<UserProfile>;
    const userProfile = getUserProfile(userProfileMap, createdBy);
    const downloadUrl = usePathUrl(userProfile?.profileImages?.[0]?.path);
    return (
        <img
            alt={createdBy}
            src={downloadUrl || '/Ellipse4.png'}
            height="60px"
            width="60px"
            style={{ marginTop: '-0px', borderRadius: '60px' }}
        />
    );
};

const Contributors = ({ commits }: { commits: Commit[] }) => (
    <div>
        {commits
            .map((commit) => commit.createdBy)
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((createBy) => (
                <ContributorsListImage key={createBy} createdBy={createBy} />
            ))}
    </div>
);

const daysAgoMessage = (date: Date, daysAgo: number) => {
    if (daysAgo === 0) {
        return 'Today';
    }
    if (daysAgo === 1) {
        return 'Yesterday';
    }
    return `${date.getDate()}/${date.getMonth()}/${date.getFullYear()}`;
};

export const DateGroup = ({ date, commits, daysAgo }: CommitDateGroup) => {
    const { expCol, setExpCol } = useCommitsPageContext();
    const key = date.toISOString();
    const expanded = expCol.getValue(key) === 'expanded';
    return (
        <div style={{ width: '100%' }}>
            <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                <div
                    style={{
                        height: '32px',
                        width: '108px',
                        borderRadius: '15px',
                        backgroundColor: '#18A0FB',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: 'white',
                        marginBottom: '20px',
                        cursor: 'default',
                    }}
                >
                    {daysAgoMessage(date, daysAgo)}
                    <div
                        style={{
                            display: 'flex',
                            marginBottom: '-1px',
                            marginRight: '-10px',
                            cursor: 'pointer',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpCol(
                                key,
                                expCol.getValue(key) === 'expanded' ? 'collapsed' : 'expanded'
                            );
                        }}
                    >
                        {expanded ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                    </div>
                </div>
                {!expanded && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ marginTop: '-18px', marginLeft: '16px' }}>
                            <div style={{ display: 'flex' }}>
                                {renderCount(commits.length, 'entry', 'entries')} modified
                            </div>
                            <div
                                style={{ color: 'rgb(27 27 27 / 50%)' }}
                            >{`${date.getDate()}/${date.getMonth()}/${date.getFullYear()}`}</div>
                        </div>
                        <div style={{ marginTop: '-8px' }}>
                            <Contributors commits={commits} />
                        </div>
                    </div>
                )}
            </div>
            {expanded ? (
                makeContributorGroups(commits).map((contributorGroup, index, contributorGroups) => {
                    return (
                        <ContributorGroup
                            date={date}
                            {...contributorGroup}
                            key={`${contributorGroup.createdBy}:${index}`}
                            isLast={index === contributorGroups.length - 1}
                        />
                    );
                })
            ) : (
                <div
                    style={{
                        width: 'calc(100% - 70px)',
                        marginBottom: '10px',
                        border: '1px solid rgb(216, 216, 216)',
                        marginLeft: '81px',
                    }}
                />
            )}
        </div>
    );
};
export const renderCount = (count: number, singuler: string, plural: string) => {
    if (count < 1) {
        return '';
    }
    if (count === 1) {
        return `1 ${singuler}`;
    }
    return `${count} ${plural}`;
};
const Action = ({ action }: { action: CommitAction }) => {
    if (action === 'add') {
        return <div style={{ padding: '0 5px', color: '#36A141' }}>added</div>;
    }
    if (action === 'delete') {
        return <div style={{ padding: '0 5px', color: '#FF0000' }}>deleted</div>;
    }
    return <div style={{ padding: '0 5px', color: '#18A0FB' }}>updated</div>;
};

export const getUserProfile = (userProfileMap: Map<string, UserProfile>, email: string) => {
    const userProfile = userProfileMap.get(email);
    if (userProfile) {
        return userProfile;
    }
    if (email.endsWith('@terminos.io')) {
        return new UserProfile({
            email,
            title: 'customer support',
            roles: [],
            name: email.split('@')[0],
            profileImages: [
                {
                    fileType: 'image/jpeg',
                    height: '170',
                    path: 'terminos_io::jqTwhCFhlT18jooAVrtx::profileImage::170x170::v0',
                    version: 'v0',
                    width: '170',
                },
            ],
        });
    }
};

interface ContributorGroupProps extends CommitContributorGroup {
    date: Date;
    isLast: boolean;
}
const ContributorGroup = ({
    createdBy,
    action,
    commits,
    date,
    isLast,
    typeIndex,
}: ContributorGroupProps) => {
    const [, profilesPending, , userProfileMap] = useCollectionContext(
        USER_PROFILE_COLLECTION_NAME
    ) as CollectionState<UserProfile>;
    const userProfile = getUserProfile(userProfileMap, createdBy) as UserProfile;
    const downloadUrl = usePathUrl(userProfile?.profileImages?.[0]?.path);
    const [entry, entries] = typeToWords(typeIndex.split('_').splice(-1)[0]);
    if (profilesPending) {
        return null;
    }
    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', width: '100%' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        marginRight: '40px',
                        marginBottom: '-12px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                        }}
                    >
                        <img
                            alt=""
                            src={downloadUrl || '/Ellipse4.png'}
                            height="60px"
                            width="60px"
                            style={{ marginTop: '-0px', borderRadius: '60px' }}
                        />
                    </div>
                    {!isLast && <div style={{ border: '1px solid #D8D8D8', flexGrow: 1 }} />}
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            width: '100%',
                            height: '60px',
                        }}
                    >
                        <div style={{ display: 'flex' }}>
                            {renderCount(commits.length, entry, entries)} <Action action={action} />{' '}
                            by
                            <div style={{ padding: '0 5px' }}>{userProfile.name}</div>
                        </div>
                        <div>{`${date.getDate()}/${date.getMonth()}/${date.getFullYear()}`}</div>
                    </div>
                    {commits.map((commit) => {
                        const commitDate = new Date((commit.createdAt?.seconds as number) * 1000);
                        const minutes = `${commitDate.getMinutes()}`.padStart(2, '0');
                        const hours = commitDate.getHours();
                        const changes = commit.changes.filter((change) =>
                            commit.typeIndex.endsWith('Clause')
                                ? change.field === 'content'
                                : change.field === 'name'
                        );
                        if (!changes.length) {
                            console.info(
                                `commit without changes to display ${JSON.stringify(commit)}`
                            );
                            return null;
                        }
                        const change = changes[0];
                        return (
                            <div
                                key={commit.id}
                                style={{
                                    minHeight: '48px',
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #D8D8D8',
                                    marginBottom: '10px',
                                    display: 'flex',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: '100px',
                                        marginTop: '20px',
                                        height: '100%',
                                    }}
                                >{`${hours}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`}</div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        minHeight: '48px',
                                        flexBasis: '100px',
                                        flexGrow: 1,
                                        height: '100%',
                                        marginTop: '16px',
                                    }}
                                >
                                    {commit.message}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexBasis: '100px',
                                        flexGrow: 1,
                                        height: '100%',
                                        whiteSpace: 'break-spaces',
                                    }}
                                >
                                    {change.before}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexBasis: '100px',
                                        flexGrow: 1,
                                        height: '100%',
                                        whiteSpace: 'break-spaces',
                                    }}
                                >
                                    {change.after}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div
                style={{
                    width: 'calc(100% - 70px)',
                    marginBottom: '10px',
                    border: '1px solid rgb(216, 216, 216)',
                    marginLeft: '81px',
                }}
            ></div>
        </div>
    );
};
